import React, { useState, useEffect, useRef } from 'react';
import { getOptimizedImageUrl } from '../utils/imageOptimization';
import './LazyImage.css';

/**
 * Lazy-loaded image component using Intersection Observer
 */
function LazyImage({ 
  src, 
  alt = '', 
  className = '', 
  style = {},
  context = 'default',
  placeholder = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"%3E%3C/svg%3E',
  ...props 
}) {
  // Ensure src is a string, not an object
  const safeSrc = typeof src === 'string' ? src : (src || '');
  
  const [imageSrc, setImageSrc] = useState(placeholder);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    if (!safeSrc || !imgRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '50px',
        threshold: 0.01
      }
    );

    observer.observe(imgRef.current);

    return () => {
      if (imgRef.current) {
        observer.unobserve(imgRef.current);
      }
      observer.disconnect();
    };
  }, [safeSrc]);

  useEffect(() => {
    if (!isInView || !safeSrc) return;

    const optimizedUrl = getOptimizedImageUrl(safeSrc, context);
    
    const img = new Image();
    img.onload = () => {
      setImageSrc(optimizedUrl);
      setIsLoaded(true);
    };
    img.onerror = () => {
      setImageSrc(safeSrc); // Fallback to original
      setIsLoaded(true);
    };
    img.src = optimizedUrl;
  }, [isInView, safeSrc, context]);

  // Ensure className is a string
  const safeClassName = typeof className === 'string' ? className : '';
  
  return (
    <div
      ref={imgRef}
      className={`lazy-image-container ${safeClassName} ${isLoaded ? 'loaded' : 'loading'}`}
      style={{
        backgroundImage: `url(${imageSrc})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor: '#f0f0f0',
        transition: 'opacity 0.3s ease',
        opacity: isLoaded ? 1 : 0.7,
        ...(typeof style === 'object' && style !== null ? style : {})
      }}
    />
  );
}

export default LazyImage;

